import { Component } from '@theme/component';

/**
 * Web component for the Myself / Gift toggle on the subscription product page.
 * Handles:
 * - Toggle between Myself and Gift modes
 * - Enabling/disabling and showing/hiding gift recipient fields
 * - Required field validation gating the ATC button
 * - "Surprise them" checkbox disabling the date picker
 * - URL param restoration for the cart edit flow
 * - URL param deep-link to Gift mode via ?mode=gift
 *
 * @typedef {Object} GiftSubscriptionFormRefs
 * @property {HTMLInputElement} myselfButton
 * @property {HTMLInputElement} giftButton
 * @property {HTMLInputElement} giftFlag
 * @property {HTMLDivElement} giftFields
 * @property {HTMLInputElement} firstName
 * @property {HTMLInputElement} lastName
 * @property {HTMLInputElement} street
 * @property {HTMLInputElement} city
 * @property {HTMLInputElement} state
 * @property {HTMLInputElement} zip
 * @property {HTMLSelectElement} country
 * @property {HTMLTextAreaElement} deliveryInstructions
 * @property {HTMLTextAreaElement} giftMessage
 * @property {HTMLInputElement} recipientEmail
 * @property {HTMLInputElement} sendOn
 * @property {HTMLDivElement} sendOnField
 * @property {HTMLInputElement} surpriseCheckbox
 * @property {HTMLSpanElement} characterCount
 * @property {HTMLDivElement} liveRegion
 *
 * @extends {Component<GiftSubscriptionFormRefs>}
 */
class GiftSubscriptionForm extends Component {
  static Mode = {
    MYSELF: 'myself',
    GIFT: 'gift',
  };

  #currentMode = GiftSubscriptionForm.Mode.MYSELF;
  #boundOnInput = null;
  #boundOnMessageInput = null;
  #appstleObserver = null;
  #deepLinkToGift = false;

  requiredRefs = [
    'myselfButton',
    'giftButton',
    'giftFlag',
    'giftFields',
    'firstName',
    'lastName',
    'street',
    'city',
    'state',
    'zip',
  ];

  /** Required gift fields that gate ATC */
  get #requiredFields() {
    return [
      this.refs.firstName,
      this.refs.lastName,
      this.refs.street,
      this.refs.city,
      this.refs.state,
      this.refs.zip,
    ];
  }

  /** All gift input fields (required + optional) */
  get #allFields() {
    return [
      this.refs.firstName,
      this.refs.lastName,
      this.refs.street,
      this.refs.city,
      this.refs.state,
      this.refs.zip,
      this.refs.country,
      this.refs.deliveryInstructions,
      this.refs.giftMessage,
      this.refs.recipientEmail,
      this.refs.sendOn,
      this.refs.surpriseCheckbox,
    ];
  }

  connectedCallback() {
    super.connectedCallback();

    this.#boundOnInput = () => this.#updateValidation();
    this.#boundOnMessageInput = () => this.#updateCharacterCount();

    // Listen for input on gift fields to update ATC state
    this.refs.giftFields.addEventListener('input', this.#boundOnInput);
    this.refs.giftMessage?.addEventListener('input', this.#boundOnMessageInput);

    // Restore from URL params if present (cart edit flow)
    this.#restoreFromParams();

    // Set date constraints
    this.#setDateConstraints();

    // Note a ?mode=gift deep-link; acted on after Appstle is ready so
    // the plan filter and selection-sync have a DOM to work against.
    const params = new URLSearchParams(window.location.search);
    this.#deepLinkToGift = params.get('mode') === 'gift';

    // Watch for Appstle's plan radios to appear, then tag wrappers,
    // sync the selling_plan to the active mode, and apply ?mode=gift.
    this.#observeAppstleWidget();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#boundOnInput) {
      this.refs.giftFields.removeEventListener('input', this.#boundOnInput);
    }
    if (this.#boundOnMessageInput) {
      this.refs.giftMessage?.removeEventListener('input', this.#boundOnMessageInput);
    }
    this.#appstleObserver?.disconnect();
    this.#appstleObserver = null;
  }

  /**
   * Handle toggle between Myself and Gift modes.
   * Called via on:change="/toggleMode/myself" or on:change="/toggleMode/gift"
   * @param {string} mode
   */
  toggleMode(mode) {
    if (!Object.values(GiftSubscriptionForm.Mode).includes(mode)) return;
    if (this.#currentMode === mode) return;

    this.#currentMode = mode;
    this.#updateFormState();
  }

  /**
   * Handle "surprise them" checkbox toggle.
   * Called via on:change="/toggleSurprise"
   */
  toggleSurprise() {
    const surprised = this.refs.surpriseCheckbox.checked;
    this.refs.sendOn.disabled = surprised;
    if (this.refs.sendOnField) {
      this.refs.sendOnField.style.opacity = surprised ? '0.4' : '1';
      this.refs.sendOnField.style.pointerEvents = surprised ? 'none' : '';
    }
    if (surprised) {
      this.refs.sendOn.value = '';
      this.refs.sendOn.removeAttribute('name');
    } else {
      this.refs.sendOn.setAttribute('name', 'properties[Gift Notification Date]');
    }
  }

  // --- Private methods ---

  #updateFormState() {
    const isGift = this.#currentMode === GiftSubscriptionForm.Mode.GIFT;

    // Toggle field visibility
    this.refs.giftFields.hidden = !isGift;

    // Update hidden gift flag
    if (isGift) {
      this.refs.giftFlag.disabled = false;
      this.refs.giftFlag.value = 'Yes';
    } else {
      this.refs.giftFlag.disabled = true;
      this.refs.giftFlag.value = '';
    }

    if (isGift) {
      this.#enableFields();
      this.refs.firstName.focus();

      if (this.refs.liveRegion) {
        this.refs.liveRegion.textContent = 'Gift recipient fields are now visible';
      }
    } else {
      this.#clearFields();
      this.#disableFields();

      if (this.refs.liveRegion) {
        this.refs.liveRegion.textContent = 'Gift recipient fields are now hidden';
      }
    }

    this.#updateValidation();
    this.#setStickyBarAttribute();
    this.#syncAppstleSellingPlan();
  }

  #enableFields() {
    for (const field of this.#allFields) {
      if (field) field.disabled = false;
    }
    for (const field of this.#requiredFields) {
      field.setAttribute('required', 'required');
    }
    // Re-apply surprise state
    if (this.refs.surpriseCheckbox.checked) {
      this.refs.sendOn.disabled = true;
    }
  }

  #disableFields() {
    for (const field of this.#allFields) {
      if (field) {
        field.disabled = true;
        field.removeAttribute('required');
      }
    }
  }

  #clearFields() {
    for (const field of this.#allFields) {
      if (!field) continue;
      if (field.type === 'checkbox') {
        field.checked = false;
      } else if (field.tagName === 'SELECT') {
        field.selectedIndex = 0;
      } else {
        field.value = '';
      }
    }
    this.#updateCharacterCount();

    // Reset surprise state
    if (this.refs.sendOnField) {
      this.refs.sendOnField.style.opacity = '1';
      this.refs.sendOnField.style.pointerEvents = '';
    }
    this.refs.sendOn.setAttribute('name', 'properties[Gift Notification Date]');
  }

  /**
   * Update the ATC button disabled state based on required field completeness.
   * The product-form.js uses form.checkValidity() which respects the required attribute,
   * but we also set a data attribute on the form for the sticky ATC bar to observe.
   */
  #updateValidation() {
    const isGift = this.#currentMode === GiftSubscriptionForm.Mode.GIFT;
    const form = this.closest('product-form-component');

    if (!isGift) {
      form?.removeAttribute('data-gift-fields-incomplete');
      return;
    }

    const allFilled = this.#requiredFields.every((f) => f.value.trim() !== '');
    if (allFilled) {
      form?.removeAttribute('data-gift-fields-incomplete');
    } else {
      form?.setAttribute('data-gift-fields-incomplete', 'true');
    }
  }

  /**
   * Set a data attribute on the section so sticky ATC can observe gift mode.
   */
  #setStickyBarAttribute() {
    const isGift = this.#currentMode === GiftSubscriptionForm.Mode.GIFT;
    const section = this.closest('.shopify-section');
    if (isGift) {
      section?.setAttribute('data-gift-mode', 'true');
    } else {
      section?.removeAttribute('data-gift-mode');
    }
  }

  #updateCharacterCount() {
    if (!this.refs.characterCount || !this.refs.giftMessage) return;
    const current = this.refs.giftMessage.value.length;
    const max = this.refs.characterCount.getAttribute('data-max') || '200';
    const template = this.refs.characterCount.getAttribute('data-template') || '[current] / [max]';
    this.refs.characterCount.textContent = template
      .replace('[current]', current.toString())
      .replace('[max]', max);
  }

  #setDateConstraints() {
    if (!this.refs.sendOn) return;
    const today = new Date();
    const maxDate = new Date();
    maxDate.setDate(today.getDate() + 90);

    const fmt = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };

    this.refs.sendOn.setAttribute('min', fmt(today));
    this.refs.sendOn.setAttribute('max', fmt(maxDate));
  }

  /**
   * Restore gift fields from URL query parameters.
   * Used when the buyer clicks "Edit gift details" in the cart.
   */
  #restoreFromParams() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('gift') !== 'true') return;

    // Switch to gift mode
    this.refs.giftButton.checked = true;
    this.refs.myselfButton.checked = false;
    this.#currentMode = GiftSubscriptionForm.Mode.GIFT;

    // Show and enable fields
    this.refs.giftFields.hidden = false;
    this.refs.giftFlag.disabled = false;
    this.refs.giftFlag.value = 'Yes';
    this.#enableFields();

    // Map URL params to field refs
    const fieldMap = {
      first_name: 'firstName',
      last_name: 'lastName',
      street: 'street',
      city: 'city',
      state: 'state',
      zip: 'zip',
      country: 'country',
      delivery_instructions: 'deliveryInstructions',
      gift_message: 'giftMessage',
      recipient_email: 'recipientEmail',
      send_on: 'sendOn',
    };

    for (const [param, refName] of Object.entries(fieldMap)) {
      const value = params.get(param);
      if (value && this.refs[refName]) {
        this.refs[refName].value = decodeURIComponent(value);
      }
    }

    // Handle surprise checkbox
    if (params.get('surprise') === 'true') {
      this.refs.surpriseCheckbox.checked = true;
      this.toggleSurprise();
    }

    // Store the cart line key for removal after re-add
    const editingKey = params.get('editing_cart_key');
    if (editingKey) {
      this.dataset.editingCartKey = editingKey;
      this.#listenForCartAdd();
    }

    this.#updateValidation();
    this.#setStickyBarAttribute();
    this.#updateCharacterCount();
  }

  /**
   * When editing a gift item from cart, listen for the cart add event
   * then remove the old line item.
   */
  #listenForCartAdd() {
    const handler = async () => {
      const key = this.dataset.editingCartKey;
      if (!key) return;

      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0 }),
        });
      } catch (e) {
        console.error('Failed to remove old cart line item:', e);
      }

      delete this.dataset.editingCartKey;
      document.removeEventListener('theme:cart:update', handler);

      // Clean up URL params
      const url = new URL(window.location.href);
      url.search = '';
      window.history.replaceState({}, '', url.toString());
    };

    // Listen for the Atelier theme cart update event
    document.addEventListener('theme:cart:update', handler);
  }

  /**
   * Watch for Appstle's plan radios to appear. Once present, tag the wrappers
   * with stable class names, click-sync the selling_plan to the current mode,
   * and inject the plain-language descriptions. One-shot; observer disconnects
   * after first successful init.
   */
  #observeAppstleWidget() {
    const recurringId = this.dataset.recurringPlanId;
    const prepaidId = this.dataset.prepaidPlanId;
    if (!recurringId && !prepaidId) return;

    const tryInit = () => {
      const radios = document.querySelectorAll('input[name="selling_plan"]');
      if (radios.length < 1) return false;
      this.#tagAppstleWrappers();
      this.#syncAppstleSellingPlan();
      this.#activateDeepLinkModeIfRequested();
      return true;
    };

    if (tryInit()) return;

    this.#appstleObserver = new MutationObserver(() => {
      if (tryInit()) {
        this.#appstleObserver.disconnect();
        this.#appstleObserver = null;
      }
    });
    this.#appstleObserver.observe(document.body, { childList: true, subtree: true });
  }

  /**
   * Tag the two Appstle plan wrappers with stable class names so the
   * mode-based CSS show/hide rules don't need to know plan IDs.
   */
  #tagAppstleWrappers() {
    const recurringId = this.dataset.recurringPlanId;
    const prepaidId = this.dataset.prepaidPlanId;
    if (recurringId) {
      const radio = document.querySelector(
        `input[name="selling_plan"][value="${recurringId}"]`
      );
      const wrapper = radio?.closest('.appstle_subscription_wrapper_option');
      wrapper?.classList.add('ewp-plan-recurring');
    }
    if (prepaidId) {
      const radio = document.querySelector(
        `input[name="selling_plan"][value="${prepaidId}"]`
      );
      const wrapper = radio?.closest('.appstle_subscription_wrapper_option');
      wrapper?.classList.add('ewp-plan-prepaid');
    }
  }

  /**
   * Click the selling_plan radio that matches the current mode so the
   * submitted plan follows the visible option. No-op if the correct radio
   * is already checked, or if Appstle hasn't rendered.
   */
  #syncAppstleSellingPlan() {
    const recurringId = this.dataset.recurringPlanId;
    const prepaidId = this.dataset.prepaidPlanId;
    const targetValue = this.#currentMode === GiftSubscriptionForm.Mode.GIFT ? prepaidId : recurringId;
    if (!targetValue) return;
    const radio = document.querySelector(
      `input[name="selling_plan"][value="${targetValue}"]`
    );
    if (radio && !radio.checked) {
      radio.click();
    }
  }

  /**
   * If the URL carried ?mode=gift, flip the Gift radio and dispatch
   * change so the existing toggleMode handler runs, same code path as
   * a user click. One-shot; guarded by the cart-edit flow which may
   * have already switched to Gift mode via #restoreFromParams().
   */
  #activateDeepLinkModeIfRequested() {
    if (!this.#deepLinkToGift) return;
    this.#deepLinkToGift = false;
    if (this.#currentMode === GiftSubscriptionForm.Mode.GIFT) return;

    this.refs.giftButton.checked = true;
    this.refs.myselfButton.checked = false;
    this.refs.giftButton.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

customElements.define('gift-subscription-form', GiftSubscriptionForm);
